import { useCallback, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import { PagedCombobox } from "@/components/ui/pagedcombobox";
import { Plus } from "lucide-react";
import { cropTypesApi, type CropType } from "../../services/cropTypesApi";
import { cropVarietiesApi, type CropVariety } from "../../services/cropVarietiesApi";
import { useNotification } from "@/shared/hooks/useNotification";
import { getFieldErrors, getErrorMessage } from "@/shared/utils/errorHandler";
import { useSubmitSetupApproval } from "../../hooks/useSetupApprovals";
import { SETUP_APPROVAL_TYPE } from "../../services/setupApprovalApi";
import { useConfirm } from "@/shared/contexts/confirm-context";

type FormData = {
  selectedCropType: CropType | null;
  selectedVariety: CropVariety | null;
  pricingMode: "perTray" | "perSeedling";
  companySeedlingPrice210: string;
  propagationServicePrice210: string;
  companySeedlingPrice442: string;
  propagationServicePrice442: string;
  companySeedlingPricePiece: string;
  propagationServicePricePiece: string;
};

const initialFormData: FormData = {
  selectedCropType: null,
  selectedVariety: null,
  pricingMode: "perTray",
  companySeedlingPrice210: "",
  propagationServicePrice210: "",
  companySeedlingPrice442: "",
  propagationServicePrice442: "",
  companySeedlingPricePiece: "",
  propagationServicePricePiece: "",
};

export const CreateVarietyPricingDialog = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const submitMutation = useSubmitSetupApproval();
  const notify = useNotification();
  const confirm = useConfirm();

  const handleChange = (
    field: keyof Omit<FormData, "selectedCropType" | "selectedVariety">,
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const fetchCropTypes = useCallback(
    ({ search, pageNumber, pageSize }: { search: string; pageNumber: number; pageSize: number }) =>
      cropTypesApi.getCropTypesPaged({ search, pageNumber, pageSize, isActive: true, sortBy: "name" }),
    [],
  );

  const cropTypeId = formData.selectedCropType?.id;
  const fetchVarieties = useCallback(
    ({ search, pageNumber, pageSize }: { search: string; pageNumber: number; pageSize: number }) =>
      cropVarietiesApi.getCropVarietiesPaged({
        search,
        pageNumber,
        pageSize,
        isActive: true,
        cropTypeId,
        sortBy: "name",
      }),
    [cropTypeId],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    if (!formData.selectedCropType) {
      notify.error("Select a crop type");
      return;
    }

    const isPerTray = formData.pricingMode === "perTray";
    const cropTypeIdLocal = formData.selectedCropType.id;
    const varietyId = formData.selectedVariety?.id ?? null;
    const cropTypeName = formData.selectedCropType.name;
    const varietyName = formData.selectedVariety?.name;

    let payload: Array<{
      cropTypeId: string;
      cropVarietyId: string | null;
      pricingMode: 1 | 2;
      trayType: number | null;
      companySeedlingPrice: number | null;
      propagationServicePrice: number | null;
    }>;

    const positiveOrNull = (raw: string): number | null => {
      const parsed = parseFloat(raw);
      return !isNaN(parsed) && parsed > 0 ? parsed : null;
    };

    if (isPerTray) {
      payload = [
        {
          trayType: 210,
          companySeedlingPrice: positiveOrNull(formData.companySeedlingPrice210),
          propagationServicePrice: positiveOrNull(formData.propagationServicePrice210),
        },
        {
          trayType: 442,
          companySeedlingPrice: positiveOrNull(formData.companySeedlingPrice442),
          propagationServicePrice: positiveOrNull(formData.propagationServicePrice442),
        },
      ]
        .filter((e) => e.companySeedlingPrice !== null || e.propagationServicePrice !== null)
        .map((e) => ({
          cropTypeId: cropTypeIdLocal,
          cropVarietyId: varietyId,
          pricingMode: 1,
          ...e,
        }));

      if (payload.length === 0) {
        notify.error("Enter at least one price (seedling or propagation) for at least one tray type");
        return;
      }
    } else {
      const seedlingPrice = positiveOrNull(formData.companySeedlingPricePiece);
      const propagationPrice = positiveOrNull(formData.propagationServicePricePiece);
      if (seedlingPrice === null && propagationPrice === null) {
        notify.error("Enter at least one price — seedling or propagation");
        return;
      }
      payload = [
        {
          cropTypeId: cropTypeIdLocal,
          cropVarietyId: varietyId,
          pricingMode: 2,
          trayType: null,
          companySeedlingPrice: seedlingPrice,
          propagationServicePrice: propagationPrice,
        },
      ];
    }

    const descriptionTarget = varietyName
      ? `${cropTypeName} — ${varietyName}`
      : cropTypeName || "(no crop type)";

    confirm({
      title: "Submit pricing for approval?",
      description: `Pricing for ${descriptionTarget} will be submitted for approval before it takes effect.`,
      confirmLabel: "Submit",
      onConfirm: () =>
        submitMutation
          .mutateAsync({
            approvalType: SETUP_APPROVAL_TYPE.Pricing,
            entityJson: JSON.stringify(payload),
            description: `New Variety Pricing: ${descriptionTarget}`,
          })
          .then(() => {
            notify.success("Submitted for approval");
            setFormData(initialFormData);
            setOpen(false);
          })
          .catch((error: unknown) => {
            const fe = getFieldErrors(error);
            if (fe) setFieldErrors(fe);
            else notify.error(getErrorMessage(error));
            throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="w-4 h-4" />
          New Variety Pricing
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Variety Pricing</DialogTitle>
          <DialogDescription>
            Add pricing for a crop variety
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Field>
            <FieldLabel htmlFor="cropTypeId">
              Crop Type <span className="text-red-500">*</span>
            </FieldLabel>
            <PagedCombobox<CropType>
              title="Crop Type"
              value={formData.selectedCropType}
              onChange={(item) =>
                setFormData((prev) => ({
                  ...prev,
                  selectedCropType: item,
                  selectedVariety: null,
                }))
              }
              fetchPage={fetchCropTypes}
              disabled={submitMutation.isPending}
            />
            {fieldErrors.cropTypeId && (
              <div className="text-red-500 text-xs mt-1">
                {fieldErrors.cropTypeId.join(", ")}
              </div>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="cropVarietyId">
              Variety <span className="text-muted-foreground font-normal text-xs">(optional)</span>
            </FieldLabel>
            <PagedCombobox<CropVariety>
              title="Variety"
              value={formData.selectedVariety}
              onChange={(item) =>
                setFormData((prev) => ({ ...prev, selectedVariety: item }))
              }
              fetchPage={fetchVarieties}
              disabled={!formData.selectedCropType || submitMutation.isPending}
            />
            {fieldErrors.cropVarietyId && (
              <div className="text-red-500 text-xs mt-1">
                {fieldErrors.cropVarietyId.join(", ")}
              </div>
            )}
          </Field>

          <Field>
            <FieldLabel>
              Pricing Mode <span className="text-red-500">*</span>
            </FieldLabel>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="pricingMode"
                  value="perTray"
                  checked={formData.pricingMode === "perTray"}
                  onChange={() => handleChange("pricingMode", "perTray")}
                  disabled={submitMutation.isPending}
                  className="accent-primary"
                />
                <span className="text-sm">Per Tray</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="pricingMode"
                  value="perSeedling"
                  checked={formData.pricingMode === "perSeedling"}
                  onChange={() => handleChange("pricingMode", "perSeedling")}
                  disabled={submitMutation.isPending}
                  className="accent-primary"
                />
                <span className="text-sm">Per Seedling/Piece</span>
              </label>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {formData.pricingMode === "perTray"
                ? "Price applies to a full tray of seedlings."
                : "Price each plant individually (e.g. KES 15 per seedling)."}
            </p>
          </Field>

          {formData.pricingMode === "perTray" ? (
            <>
              <div className="space-y-3">
                <div className="text-sm font-medium text-muted-foreground">210-Hole Tray</div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
                  <Field>
                    <FieldLabel htmlFor="companySeedlingPrice210">
                      Seedling Price
                    </FieldLabel>
                    <Input
                      id="companySeedlingPrice210"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={formData.companySeedlingPrice210}
                      onChange={(e) => handleChange("companySeedlingPrice210", e.target.value)}
                      disabled={submitMutation.isPending}
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="propagationServicePrice210">
                      Propagation Price
                    </FieldLabel>
                    <Input
                      id="propagationServicePrice210"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={formData.propagationServicePrice210}
                      onChange={(e) => handleChange("propagationServicePrice210", e.target.value)}
                      disabled={submitMutation.isPending}
                    />
                  </Field>
                </div>
              </div>

              <div className="space-y-3">
                <div className="text-sm font-medium text-muted-foreground">442-Hole Tray</div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
                  <Field>
                    <FieldLabel htmlFor="companySeedlingPrice442">
                      Seedling Price
                    </FieldLabel>
                    <Input
                      id="companySeedlingPrice442"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={formData.companySeedlingPrice442}
                      onChange={(e) => handleChange("companySeedlingPrice442", e.target.value)}
                      disabled={submitMutation.isPending}
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="propagationServicePrice442">
                      Propagation Price
                    </FieldLabel>
                    <Input
                      id="propagationServicePrice442"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={formData.propagationServicePrice442}
                      onChange={(e) => handleChange("propagationServicePrice442", e.target.value)}
                      disabled={submitMutation.isPending}
                    />
                  </Field>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Fill in prices for one or both tray types. Leave a price blank to mark that side as <span className="font-medium">not offered</span>. A tray type is created if at least one of its prices is set.
              </p>
            </>
          ) : (
            <div className="space-y-3">
              <div className="text-sm font-medium text-muted-foreground">Per Seedling/Piece</div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
                <Field>
                  <FieldLabel htmlFor="companySeedlingPricePiece">
                    Price per Seedling/Piece
                  </FieldLabel>
                  <Input
                    id="companySeedlingPricePiece"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.companySeedlingPricePiece}
                    onChange={(e) => handleChange("companySeedlingPricePiece", e.target.value)}
                    disabled={submitMutation.isPending}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="propagationServicePricePiece">
                    Propagation Price
                  </FieldLabel>
                  <Input
                    id="propagationServicePricePiece"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.propagationServicePricePiece}
                    onChange={(e) => handleChange("propagationServicePricePiece", e.target.value)}
                    disabled={submitMutation.isPending}
                  />
                </Field>
              </div>
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={
              submitMutation.isPending ||
              !formData.selectedCropType ||
              (formData.pricingMode === "perTray"
                ? !formData.companySeedlingPrice210 &&
                  !formData.companySeedlingPrice442 &&
                  !formData.propagationServicePrice210 &&
                  !formData.propagationServicePrice442
                : !formData.companySeedlingPricePiece &&
                  !formData.propagationServicePricePiece)
            }
          >
            {submitMutation.isPending ? "Saving..." : "Save Pricing"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
